import React, { useEffect } from 'react'
import { logBreadcrumb, logError } from '../../services/errorHandler'

// Read at module load so a misconfigured env var fails loud rather than
// silently redirecting users to `window.location.replace("undefined")`.
const COACHEE_WEB_URL = import.meta.env.VITE_APP_COACHEE_WEB_URL
const IS_VALID_TARGET =
    typeof COACHEE_WEB_URL === 'string' &&
    /^https?:\/\//.test(COACHEE_WEB_URL)

/**
 * Catch-all for any URL the SPA doesn't have a registered route for. Redirects
 * via `window.location.replace` to the env-specific coachee-web base URL,
 * with no path or query preserved (intentional — see Edge Cases in the plan).
 *
 * If the env var is missing or malformed at build time, we log to Sentry and
 * render a minimal in-page message rather than firing a broken redirect.
 */
export const CoacheeWebRedirect: React.FC = () => {
    useEffect(() => {
        if (!IS_VALID_TARGET) {
            logError(
                new Error(
                    'CoacheeWebRedirect: VITE_APP_COACHEE_WEB_URL is missing or malformed',
                ),
            )
            return
        }
        // Log a breadcrumb (no URL data — token-leakage guard) so we can
        // observe unexpected URLs hitting the SPA in production.
        logBreadcrumb('unknown_route_redirect')
        window.location.replace(COACHEE_WEB_URL as string)
    }, [])

    if (!IS_VALID_TARGET) {
        return (
            <main
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '24px',
                    textAlign: 'center',
                }}
            >
                <p>
                    Redirect unavailable — please go to{' '}
                    <a href="https://coachee.sama.io">coachee.sama.io</a>.
                </p>
            </main>
        )
    }

    return <main aria-hidden="true" />
}

export default CoacheeWebRedirect
